import { ThemeConfig, PRESET_THEMES } from '@/types/theme';

/**
 * Get a theme-aware color class name
 * @param colorType - The type of color to get (primary, secondary, accent, etc.)
 * @param variant - The variant of the color (default, hover, light, etc.)
 * @returns Tailwind CSS class name
 */
export const getThemeColorClass = (colorType: string, variant: string = 'default'): string => {
  const colorMap: Record<string, Record<string, string>> = {
    primary: {
      default: 'text-primary',
      hover: 'hover:text-primaryHover',
      bg: 'bg-primary',
      bgHover: 'hover:bg-primaryHover',
      border: 'border-primary',
      ring: 'ring-primary',
    },
    secondary: {
      default: 'text-secondary',
      hover: 'hover:text-secondaryHover',
      bg: 'bg-secondary',
      bgHover: 'hover:bg-secondary/90',
      border: 'border-secondary',
      ring: 'ring-secondary',
    },
    accent: {
      default: 'text-accent',
      hover: 'hover:text-accentHover',
      bg: 'bg-accent',
      bgHover: 'hover:bg-accent/90',
      border: 'border-accent',
      ring: 'ring-accent',
    },
  };

  return colorMap[colorType]?.[variant] || '';
};

/**
 * Get a theme-aware background color class
 * @param colorType - The type of color to get
 * @param variant - The variant of the color
 * @returns Tailwind CSS class name
 */
export const getThemeBgClass = (colorType: string, variant: string = 'default'): string => {
  return getThemeColorClass(colorType, `bg${variant.charAt(0).toUpperCase() + variant.slice(1)}`);
};

/**
 * Get a theme-aware border color class
 * @param colorType - The type of color to get
 * @returns Tailwind CSS class name
 */
export const getThemeBorderClass = (colorType: string): string => {
  return getThemeColorClass(colorType, 'border');
};

/**
 * Get a theme-aware ring color class
 * @param colorType - The type of color to get
 * @returns Tailwind CSS class name
 */
export const getThemeRingClass = (colorType: string): string => {
  return getThemeColorClass(colorType, 'ring');
};

/**
 * Apply theme colors to a CSS element
 * @param element - The DOM element to apply colors to
 * @param theme - The theme configuration
 */
export const applyThemeToElement = (element: HTMLElement, theme: ThemeConfig): void => {
  Object.entries(theme.colors).forEach(([key, value]) => {
    element.style.setProperty(`--${key}`, value);
  });
};

/**
 * Get a CSS custom property value
 * @param propertyName - The name of the CSS custom property
 * @returns The computed value of the CSS custom property
 */
export const getCSSVariable = (propertyName: string): string => {
  return getComputedStyle(document.documentElement).getPropertyValue(`--${propertyName}`).trim();
};

/**
 * Set a CSS custom property value
 * @param propertyName - The name of the CSS custom property
 * @param value - The value to set
 */
export const setCSSVariable = (propertyName: string, value: string): void => {
  document.documentElement.style.setProperty(`--${propertyName}`, value);
};

/**
 * Generate a theme-aware class name for common UI patterns
 * @param pattern - The UI pattern (button, card, input, etc.)
 * @param variant - The variant of the pattern (primary, secondary, etc.)
 * @returns Tailwind CSS class name
 */
export const getThemePatternClass = (pattern: string, variant: string = 'default'): string => {
  const patterns: Record<string, Record<string, string>> = {
    button: {
      primary: 'bg-primary hover:bg-primaryHover text-white',
      secondary: 'bg-secondary hover:bg-secondary/90 text-white',
      outline: 'border border-primary text-primary hover:bg-primary hover:text-white',
      ghost: 'text-primary hover:bg-primaryLight',
    },
    card: {
      default: 'bg-card text-cardForeground border border-border',
      elevated: 'bg-card text-cardForeground border border-border shadow-lg',
    },
    input: {
      default: 'bg-input border border-border focus:ring-2 focus:ring-ring',
      error: 'bg-input border border-destructive focus:ring-2 focus:ring-destructive',
    },
  };

  return patterns[pattern]?.[variant] || '';
};

/**
 * Check if a color is light or dark
 * @param color - The color in hex format
 * @returns 'light' or 'dark'
 */
export const getColorContrast = (color: string): 'light' | 'dark' => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? 'light' : 'dark';
};

/**
 * Generate a hover color based on the base color
 * @param color - The base color in hex format
 * @param factor - The darkening factor (0-1)
 * @returns The hover color in hex format
 */
export const generateHoverColor = (color: string, factor: number = 0.1): string => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Darken the color
  const newR = Math.max(0, Math.floor(r * (1 - factor)));
  const newG = Math.max(0, Math.floor(g * (1 - factor)));
  const newB = Math.max(0, Math.floor(b * (1 - factor)));
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}; 

/**
 * Get appropriate text color (light or dark) for a given background color
 * @param backgroundColor - The background color in hex format
 * @returns The appropriate text color in hex format ('#000000' for dark text, '#ffffff' for light text)
 */
export const getContrastingTextColor = (backgroundColor: string): string => {
  const contrast = getColorContrast(backgroundColor);
  return contrast === 'light' ? '#000000' : '#ffffff';
};

/**
 * Get appropriate text color with custom light/dark options
 * @param backgroundColor - The background color in hex format
 * @param lightTextColor - Custom light text color (default: '#000000')
 * @param darkTextColor - Custom dark text color (default: '#ffffff')
 * @returns The appropriate text color in hex format
 */
export const getContrastingTextColorCustom = (
  backgroundColor: string, 
  lightTextColor: string = '#000000', 
  darkTextColor: string = '#ffffff'
): string => {
  const contrast = getColorContrast(backgroundColor);
  return contrast === 'light' ? lightTextColor : darkTextColor;
};

/**
 * Validate theme colors for contrast issues and suggest fixes
 * @param theme - The theme configuration to validate
 * @returns Object containing validation results and suggestions
 */
export const validateThemeContrast = (theme: ThemeConfig): {
  isValid: boolean;
  issues: Array<{
    type: 'background-text' | 'border-text' | 'input-text';
    element: string;
    backgroundColor: string;
    textColor: string;
    suggestion: string;
  }>;
  suggestions: string[];
} => {
  const issues: Array<{
    type: 'background-text' | 'border-text' | 'input-text';
    element: string;
    backgroundColor: string;
    textColor: string;
    suggestion: string;
  }> = [];
  
  const suggestions: string[] = [];
  
  // Check primary colors
  const primaryContrast = getColorContrast(theme.colors.primary);
  const primaryTextContrast = getColorContrast(theme.colors.primaryForeground || '#ffffff');
  
  if (primaryContrast === primaryTextContrast) {
    issues.push({
      type: 'background-text',
      element: 'primary',
      backgroundColor: theme.colors.primary,
      textColor: theme.colors.primaryForeground || '#ffffff',
      suggestion: `Primary text should be ${primaryContrast === 'light' ? 'dark' : 'light'} for better contrast`
    });
  }
  
  // Check secondary colors
  const secondaryContrast = getColorContrast(theme.colors.secondary);
  const secondaryTextContrast = getColorContrast(theme.colors.secondaryForeground || '#ffffff');
  
  if (secondaryContrast === secondaryTextContrast) {
    issues.push({
      type: 'background-text',
      element: 'secondary',
      backgroundColor: theme.colors.secondary,
      textColor: theme.colors.secondaryForeground || '#ffffff',
      suggestion: `Secondary text should be ${secondaryContrast === 'light' ? 'dark' : 'light'} for better contrast`
    });
  }
  
  // Check card colors
  const cardContrast = getColorContrast(theme.colors.card);
  const cardTextContrast = getColorContrast(theme.colors.cardForeground);
  
  if (cardContrast === cardTextContrast) {
    issues.push({
      type: 'background-text',
      element: 'card',
      backgroundColor: theme.colors.card,
      textColor: theme.colors.cardForeground,
      suggestion: `Card text should be ${cardContrast === 'light' ? 'dark' : 'light'} for better contrast`
    });
  }
  
  // Check destructive colors
  const destructiveContrast = getColorContrast(theme.colors.destructive);
  const destructiveTextContrast = getColorContrast(theme.colors.destructiveForeground);
  
  if (destructiveContrast === destructiveTextContrast) {
    issues.push({
      type: 'background-text',
      element: 'destructive',
      backgroundColor: theme.colors.destructive,
      textColor: theme.colors.destructiveForeground,
      suggestion: `Destructive text should be ${destructiveContrast === 'light' ? 'dark' : 'light'} for better contrast`
    });
  }
  
  // Check sidebar colors
  const sidebarContrast = getColorContrast(theme.colors.sidebarColor);
  const sidebarTextContrast = getColorContrast(theme.colors.sidebarTextColor);
  
  if (sidebarContrast === sidebarTextContrast) {
    issues.push({
      type: 'background-text',
      element: 'sidebar',
      backgroundColor: theme.colors.sidebarColor,
      textColor: theme.colors.sidebarTextColor,
      suggestion: `Sidebar text should be ${sidebarContrast === 'light' ? 'dark' : 'light'} for better contrast`
    });
  }
  
  // Generate suggestions for fixing issues
  if (issues.length > 0) {
    suggestions.push('Consider using getContrastingTextColor() to automatically generate appropriate text colors');
    suggestions.push('Use getContrastingTextColorCustom() for custom light/dark text color options');
  }
  
  return {
    isValid: issues.length === 0,
    issues,
    suggestions
  };
};

/**
 * Auto-fix theme contrast issues by generating appropriate text colors
 * @param theme - The theme configuration to fix
 * @returns A new theme configuration with fixed contrast issues
 */
export const autoFixThemeContrast = (theme: ThemeConfig): ThemeConfig => {
  const fixedTheme = { ...theme };
  const colors = { ...theme.colors };
  
  // Fix primary colors
  if (!colors.primaryForeground) {
    colors.primaryForeground = getContrastingTextColor(colors.primary);
  }
  
  // Fix secondary colors
  if (!colors.secondaryForeground) {
    colors.secondaryForeground = getContrastingTextColor(colors.secondary);
  }
  
  // Fix destructive colors
  if (!colors.destructiveForeground) {
    colors.destructiveForeground = getContrastingTextColor(colors.destructive);
  }
  
  // Fix sidebar colors
  if (!colors.sidebarTextColor) {
    colors.sidebarTextColor = getContrastingTextColor(colors.sidebarColor);
  }
  
  // Fix sidebar hover colors
  if (!colors.sidebarHoverTextColor) {
    colors.sidebarHoverTextColor = getContrastingTextColor(colors.sidebarHoverBgColor);
  }
  
  fixedTheme.colors = colors;
  return fixedTheme;
};

/**
 * Generate a safe button class that ensures proper contrast
 * @param backgroundColor - The background color in hex format
 * @param customLightText - Custom light text color (optional)
 * @param customDarkText - Custom dark text color (optional)
 * @returns Tailwind CSS classes for a button with proper contrast
 */
export const getSafeButtonClasses = (
  backgroundColor: string,
  customLightText: string = '#000000',
  customDarkText: string = '#ffffff'
): string => {
  const textColor = getContrastingTextColorCustom(backgroundColor, customLightText, customDarkText);
  const hoverColor = generateHoverColor(backgroundColor, 0.1);
  const hoverTextColor = getContrastingTextColorCustom(hoverColor, customLightText, customDarkText);
  
  return `bg-[${backgroundColor}] text-[${textColor}] hover:bg-[${hoverColor}] hover:text-[${hoverTextColor}]`;
};

/**
 * Generate a safe card class that ensures proper contrast
 * @param backgroundColor - The background color in hex format
 * @param customLightText - Custom light text color (optional)
 * @param customDarkText - Custom dark text color (optional)
 * @returns Tailwind CSS classes for a card with proper contrast
 */
export const getSafeCardClasses = (
  backgroundColor: string,
  customLightText: string = '#000000',
  customDarkText: string = '#ffffff'
): string => {
  const textColor = getContrastingTextColorCustom(backgroundColor, customLightText, customDarkText);
  
  return `bg-[${backgroundColor}] text-[${textColor}] border border-border`;
};

/**
 * Apply theme settings directly to DOM CSS custom properties
 * This is used for pre-authentication theme loading
 * @param themeSettings - The theme settings object with theme data
 */
export const applyThemeToDOM = (themeSettings: any): void => {
  if (!themeSettings) return;

  const root = document.documentElement;

  // Apply custom logo and favicon if provided
  if (themeSettings.customLogo) {
    root.style.setProperty("--logo-url", `url(${themeSettings.customLogo})`);
  }
  
  if (themeSettings.customFavicon) {
    root.style.setProperty("--favicon-url", `url(${themeSettings.customFavicon})`);
    
    // Update favicon in head
    const faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (faviconLink) {
      faviconLink.href = themeSettings.customFavicon;
    }
  }

  // Apply theme colors if available
  if (themeSettings.currentTheme) {
    let currentTheme = null;
    
    // First check custom themes
    if (themeSettings.customThemes) {
      currentTheme = themeSettings.customThemes.find((theme: any) => theme.id === themeSettings.currentTheme);
    }
    
    // If not found in custom themes, check preset themes
    if (!currentTheme) {
      currentTheme = PRESET_THEMES.find((theme: any) => theme.id === themeSettings.currentTheme);
    }
    
    if (currentTheme?.colors) {
      // Apply color variables to CSS custom properties
      Object.entries(currentTheme.colors).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value as string);
      });
      
    }
  }

  // Apply SEO settings if provided
  if (themeSettings.customSEO) {
    if (themeSettings.customSEO.pageTitle) {
      document.title = themeSettings.customSEO.pageTitle;
    }
    
    // Update meta description
    if (themeSettings.customSEO.metaDescription) {
      let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.getElementsByTagName('head')[0].appendChild(metaDescription);
      }
      metaDescription.content = themeSettings.customSEO.metaDescription;
    }
  }
}; 